import React from 'react';
import { useDisputesSla } from '../../../hooks/admin/useDisputesSla';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertTriangle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function SlaWatchTable({ onOpenDispute }) {
  const { data, isLoading, error } = useDisputesSla(72);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            SLA Watch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle>SLA Watch</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-800">Failed to load SLA watch data.</p>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.items || data.items.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-green-500" />
            SLA Watch
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">✓ No disputes approaching or past SLO deadline (72h)</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          SLA Watch ({data.items.length})
        </CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          Items approaching or past decision SLO (72h)
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dispute</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Open Hrs</TableHead>
                <TableHead className="text-right">Deadline (CT)</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item) => {
                const isOverdue = item.open_hours > 72;
                return (
                  <TableRow key={item.dispute_id} className={isOverdue ? 'bg-red-50' : ''}>
                    <TableCell className="font-medium">
                      <span className="text-xs text-gray-500">{item.dispute_id.slice(0, 8)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.order_id?.slice(0, 8) || '—'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{item.seller_name || 'Unknown'}</span>
                        {item.tier && (
                          <Badge variant="outline" className="text-xs">
                            T{item.tier}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{item.buyer_name || 'Unknown'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {isOverdue ? (
                        <span className="text-red-600">{item.open_hours.toFixed(1)}h</span>
                      ) : (
                        <span>{item.open_hours.toFixed(1)}h</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {item.deadline ? new Date(item.deadline).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpenDispute(item.dispute_id)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
